import { useEffect, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { GlobeAltIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAuth } from '@restworld/feature-authentication';
import { closeAllToasts, showToast, Button as ButtonV2 } from '@restworld/ui-ds-base';
import { cn, CUSTOM_EMPLOYER_BASE_URL } from '@restworld/utils-common';

import { toggleVenuePublicConfirmAtom } from '../venue-public-confirm-modal';
import { restaurantDetailsAtom } from '..';

const PreviewCard = ({ slug }: { slug?: string }) => {
  const router = useRouter();
  const { t } = useTranslation('restaurant-showcase-v2');
  const venuePublicConfirmToggler = useSetAtom(toggleVenuePublicConfirmAtom);
  const isRestaurantPublic = useAtomValue(restaurantDetailsAtom)?.showRestaurantDetails;
  const isOffline = useMemo(() => !isRestaurantPublic, [isRestaurantPublic]);

  return (
    <div onClick={(e) => e.stopPropagation()} className="flex flex-col gap-4">
      <StatusSection>
        <StatusDot $isOffline={isOffline} />
        <StatusText>
          {isOffline ? (
            <>
              {t('your_showcase_is')} <strong>{t('offline')}</strong>. {t('publish_to_receive_candidates')}
            </>
          ) : (
            <>
              {t('your_showcase_is')} <strong>{t('online')}</strong>. {t('how_it_appears_to_candidates')}
            </>
          )}
        </StatusText>
      </StatusSection>
      <div className="flex flex-row gap-3 w-full justify-center">
        {!isRestaurantPublic && (
          <ButtonV2
            onClick={(e) => {
              e.stopPropagation();
              if (isRestaurantPublic) {
                router.push(`${CUSTOM_EMPLOYER_BASE_URL}/employer/venues/${slug}/information`);
              } else venuePublicConfirmToggler(true);
            }}
            fullwidth
            StartIcon={GlobeAltIcon}
            variant="secondary-reversed"
          >
            {t('publish')}
          </ButtonV2>
        )}
        <ButtonV2
          fullwidth
          StartIcon={PencilSquareIcon}
          onClick={(e) => {
            e.stopPropagation();
            router.push(`${CUSTOM_EMPLOYER_BASE_URL}/employer/venues/${slug}/information`);
          }}
          variant="secondary-reversed"
        >
          {t('customize')}
        </ButtonV2>
      </div>
    </div>
  );
};
export const useShowCasePreviewCard = ({ slug }: { slug?: string }) => {
  const { user } = useAuth();
  const isOwner = useIsRestaurantOwner();
  useEffect(() => {
    if ((user?.type === 'employer' || user?.type === 'employer_contact') && isOwner) {
      closeAllToasts();
      showToast(<PreviewCard slug={slug} />, 'reversed', null, true, true, 'rounded-2xl');
    }
  }, [isOwner, slug, user?.type]);
};

export const useIsRestaurantOwner = () => {
  const { user } = useAuth();
  const restaurantDetails = useAtomValue(restaurantDetailsAtom);
  const isRestaurantOwner = useMemo(
    () => user?.organization_id === restaurantDetails?.organization?.id,
    [restaurantDetails?.organization?.id, user?.organization_id]
  );
  return isRestaurantOwner;
};

const StatusSection = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center gap-2', className)} {...props} />
);

const StatusDot = ({
  $isOffline,
  className,
  ...props
}: { $isOffline: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'w-4 h-4 rounded-full flex-shrink-0',
      $isOffline ? 'background: #CC0022;' : 'background: #007740;',
      className
    )}
    {...props}
  />
);

const StatusText = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('text-base font-normal flex-1 text-[#FAFAFA] [&_strong]:font-bold', className)} {...props} />
);
